import { useEffect, useRef, useState } from 'react';

interface DemoPlayerProps {
  poster: string;
  title: string;
  duration: string;
}

export default function DemoPlayer({ poster, title, duration }: DemoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState('');
  const noticeTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 0.6));
    }, 160);
    return () => window.clearInterval(timer);
  }, [playing]);

  useEffect(() => {
    return () => {
      if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    };
  }, []);

  const showNotice = (message: string) => {
    setNotice(message);
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(''), 2600);
  };

  const handleToggle = () => {
    if (!playing) {
      showNotice('演示模式：本站不提供真实播放内容');
    }
    setPlaying((prev) => !prev);
  };

  const formatProgress = () => {
    const total = duration.split(':').map((item) => Number(item) || 0);
    const seconds = total.length === 2 ? total[0] * 60 + total[1] : 0;
    const elapsed = Math.floor((seconds * progress) / 100);
    const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
    const ss = String(elapsed % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  return (
    <div>
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-primary-950">
        <img
          src={poster}
          alt={`${title} 演示播放器封面`}
          title={`${title} 播放演示`}
          className={`h-full w-full object-cover object-top transition-all duration-700 ${
            playing ? 'scale-105 opacity-30' : 'opacity-45'
          }`}
        />
        <div className="absolute inset-0 bg-primary-950/55" />

        <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded bg-background-50/15 px-2 py-1 text-[10px] text-background-100 backdrop-blur">
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-movie-2-line text-[11px]" />
          </span>
          演示播放器
        </div>
        <span className="absolute right-3 top-3 rounded bg-accent-500/90 px-1.5 py-0.5 text-[10px] font-medium text-primary-950">
          18+
        </span>

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          {playing ? (
            <div className="flex h-12 items-end gap-1.5">
              {[0, 1, 2, 3, 4, 5].map((bar) => (
                <span
                  key={bar}
                  className="bar-wave block w-1.5 rounded-full bg-accent-500"
                  style={{ height: `${18 + (bar % 3) * 10}px`, animationDelay: `${bar * 0.12}s` }}
                />
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={handleToggle}
              aria-label="播放演示"
              className="relative flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform hover:scale-105"
            >
              <span className="absolute inset-0 animate-soft-pulse rounded-full bg-accent-500/50" />
              <i className="ri-play-fill relative text-2xl" />
            </button>
          )}

          <p className="text-sm font-medium text-background-50">演示示范 · 暂无真实内容播放</p>
          <p className="max-w-[300px] text-[11px] leading-5 text-background-300">
            此区域为前端界面演示，未接入任何真实视频接口，不提供播放权限或下载入口。
          </p>
        </div>

        <div className="absolute inset-x-0 bottom-0 px-3 pb-3">
          <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-background-50/20">
            <div
              className="h-full rounded-full bg-accent-500 transition-[width] duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center gap-2 text-background-100">
            <button
              type="button"
              onClick={handleToggle}
              aria-label={playing ? '暂停' : '播放'}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors hover:bg-background-50/15"
            >
              <i className={playing ? 'ri-pause-fill text-base' : 'ri-play-fill text-base'} />
            </button>
            <button
              type="button"
              onClick={() => {
                setMuted((prev) => !prev);
                showNotice(muted ? '演示音轨：已恢复' : '演示音轨：已静音');
              }}
              aria-label="音量"
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors hover:bg-background-50/15"
            >
              <i className={muted ? 'ri-volume-mute-line text-sm' : 'ri-volume-up-line text-sm'} />
            </button>
            <span className="text-[11px] tabular-nums text-background-200">
              {formatProgress()} / {duration}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <span className="rounded border border-background-50/25 px-1.5 py-0.5 text-[10px] text-background-200">
                演示
              </span>
              <button
                type="button"
                onClick={() => showNotice('演示模式下暂不支持全屏播放')}
                aria-label="全屏"
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors hover:bg-background-50/15"
              >
                <i className="ri-fullscreen-line text-sm" />
              </button>
            </div>
          </div>
        </div>

        {notice && (
          <div className="pointer-events-none absolute bottom-14 left-1/2 -translate-x-1/2 rounded-full bg-background-50/95 px-3 py-1.5 text-[11px] text-foreground-800">
            {notice}
          </div>
        )}
      </div>

      <p className="mt-2.5 flex items-start gap-1.5 text-[11px] leading-5 text-foreground-500">
        <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <i className="ri-information-line text-xs" />
        </span>
        播放器为演示组件：进度、音量与全屏操作仅用于展示界面状态，不会加载真实视频流。
      </p>
    </div>
  );
}